/**
 * What the skipper records without a connection waits here until it can be sent.
 *
 * - An entry is stored on the device before the screen says it is saved; if that fails the person
 *   is told it is not saved.
 * - Each entry has an operation id: the server carries it out once, so a repeated send (a lost
 *   answer, two tabs) does not write it twice.
 * - Entries are sent in the order they were made. An entry the server refuses is not dropped: it
 *   waits for the person (needs review), and the entries that depend on it wait too, while
 *   independent entries go on. A lost connection stops the sending and loses nothing.
 * - An entry belongs to the user who made it and is sent only for that user.
 */

import { all, available, get, put, remove } from "./db";
import { ApiError, NoConnectionError } from "../services/api-error";

export type QueueKind = "note" | "fuel_level" | "bunkering" | "engine" | "drill" | "checklist";
export type QueueStatus = "pending" | "syncing" | "needs_review" | "failed";

export interface QueueItem {
  operationId: string;
  userId: string;
  createdAt: string;
  kind: QueueKind;
  /** what the person did, in words, for the list */
  label: string;
  endpoint: string;
  body: Record<string, unknown>;
  /** entries in the same group depend on each other and are sent in order */
  groupKey: string;
  status: QueueStatus;
  attempts: number;
  nextTryAt?: string;
  lastError?: string;
  errorCode?: string;
  details?: unknown;
  appVersion: string;
}

export const newOperationId = (): string => `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 6)}`;

let lastStamp = 0;
/** The time an entry was made, never the same twice: entries made in the same millisecond keep the order they were made in. */
const stamp = (): string => {
  const n = Math.max(Date.now(), lastStamp + 1);
  lastStamp = n;
  return new Date(n).toISOString();
};

/** Store an entry on the device. It rejects when it could not be stored: then the entry is NOT saved. */
export async function enqueue(item: Omit<QueueItem, "status" | "attempts" | "createdAt" | "appVersion"> & { createdAt?: string }): Promise<QueueItem> {
  if (!available()) throw new Error("This device cannot store the entry for later: it is not saved");
  const full: QueueItem = { ...item, createdAt: item.createdAt ?? stamp(), status: "pending", attempts: 0, appVersion: __APP_VERSION__ };
  try {
    await put("queue", full);
    // read it back: only now is it stored
    if (!(await get<QueueItem>("queue", full.operationId))) throw new Error("not stored");
  } catch (e) {
    throw new Error(`The entry could not be stored on the device and is NOT saved (${e instanceof Error ? e.message : "storage error"})`);
  }
  return full;
}

export const listQueue = async (userId?: string): Promise<QueueItem[]> => {
  if (!available()) return [];
  try {
    const items = await all<QueueItem>("queue");
    return items.filter((i) => !userId || i.userId === userId).sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.operationId.localeCompare(b.operationId));
  } catch {
    return [];
  }
};

export const removeItem = (operationId: string) => remove("queue", operationId);
export const saveItem = (item: QueueItem) => put("queue", item);

/** Change an entry that is still in the queue. An entry the person removed in the meantime is not brought back. */
async function update(operationId: string, patch: Partial<QueueItem>): Promise<void> {
  const current = await get<QueueItem>("queue", operationId);
  if (current) await put("queue", { ...current, ...patch });
}

const BACKOFF_BASE_MS = 30_000;
const BACKOFF_MAX_MS = 15 * 60_000;
const SYNCING_STALE_MS = 2 * 60_000;

/** The entries that may be sent now, in order: not held by an earlier entry of the same group that waits for the person. */
export function sendable(items: QueueItem[], now = new Date()): QueueItem[] {
  const held = new Set<string>();
  const out: QueueItem[] = [];
  for (const it of [...items].sort((a, b) => a.createdAt.localeCompare(b.createdAt))) {
    if (held.has(it.groupKey)) continue;
    if (it.status === "needs_review") {
      held.add(it.groupKey);
      continue;
    }
    if (it.status === "syncing" && now.getTime() - Date.parse(it.nextTryAt ?? it.createdAt) < SYNCING_STALE_MS) {
      held.add(it.groupKey); // another tab is on it
      continue;
    }
    if (it.status === "failed" && it.nextTryAt && Date.parse(it.nextTryAt) > now.getTime()) {
      held.add(it.groupKey); // keep the order: the later ones wait for this one
      continue;
    }
    out.push(it);
  }
  return out;
}

export type Outcome = { kind: "stop_offline" } | { kind: "stop_session" } | { kind: "review"; message: string; code?: string; details?: unknown } | { kind: "retry"; message: string };

/** What an error from sending means for the entry. */
export function classify(error: unknown): Outcome {
  if (error instanceof NoConnectionError) return { kind: "stop_offline" };
  if (error instanceof Error && /session expired/i.test(error.message)) return { kind: "stop_session" };
  if (error instanceof ApiError) {
    const s = error.status ?? 0;
    if (s >= 500) return { kind: "retry", message: error.message };
    return { kind: "review", message: error.message, code: error.code, details: error.details };
  }
  if (error instanceof TypeError) return { kind: "stop_offline" };
  return { kind: "retry", message: error instanceof Error ? error.message : String(error) };
}

export const backoffMs = (attempts: number): number => Math.min(BACKOFF_BASE_MS * 2 ** Math.max(0, attempts - 1), BACKOFF_MAX_MS);

export interface SyncSummary {
  sent: number;
  review: number;
  failed: number;
  stopped?: "offline" | "session";
}

/**
 * Send what can be sent, one entry at a time, in order. `send` does the request with the operation id.
 * Returns what happened; the entries that were not sent are still in the queue.
 */
export async function syncQueue(userId: string, send: (item: QueueItem) => Promise<unknown>, now = () => new Date()): Promise<SyncSummary> {
  const summary: SyncSummary = { sent: 0, review: 0, failed: 0 };
  let guard = 0;
  while (guard++ < 1000) {
    const items = await listQueue(userId);
    const next = sendable(items, now())[0];
    if (!next) break;
    await update(next.operationId, { status: "syncing", nextTryAt: now().toISOString() });
    try {
      await send(next);
      await removeItem(next.operationId);
      summary.sent++;
    } catch (e) {
      const o = classify(e);
      if (o.kind === "stop_offline" || o.kind === "stop_session") {
        await update(next.operationId, { status: "pending", nextTryAt: undefined });
        summary.stopped = o.kind === "stop_offline" ? "offline" : "session";
        return summary;
      }
      if (o.kind === "review") {
        await update(next.operationId, { status: "needs_review", attempts: next.attempts + 1, lastError: o.message, errorCode: o.code, details: o.details, nextTryAt: undefined });
        summary.review++;
      } else {
        const attempts = next.attempts + 1;
        await update(next.operationId, { status: "failed", attempts, lastError: o.message, nextTryAt: new Date(now().getTime() + backoffMs(attempts)).toISOString() });
        summary.failed++;
      }
    }
  }
  return summary;
}
